/**
 * Centralized color palettes for visualizations
 *
 * Each palette is a function that maps a value t (0-1) to an RGB color.
 * t=0 typically represents "in set" (black), t>0 represents escape iterations.
 */

export type ColorFunction = (t: number) => [number, number, number];

/**
 * Color with optional weight (default 1). Higher weight = more space in gradient.
 *
 * Weight scoring guide (1-10 scale):
 *   10 - dominant color, appears most frequently
 *   7-8 - very common, major presence
 *   5-6 - moderate frequency
 *   3-4 - sparse, occasional appearance
 *   1-2 - accent color, rare/thin stripes
 */
type WeightedColor = [number, number, number, number?]; // [r, g, b, weight?]

// Mid-century modern palette - earthy, muted tones
const midcenturyColors: WeightedColor[] = [
  [35, 31, 32],     // charcoal black
  [193, 154, 107],  // tan/camel
  [101, 116, 86],   // olive green
  [198, 93, 51],    // burnt orange
  [218, 189, 139],  // cream/sand
  [139, 90, 43],    // brown
  [64, 97, 93],     // teal
  [184, 115, 51],   // copper
];

// Bridget Riley "Shadow Play" inspired palette - bold, vibrant stripes
// Weights 1-10 based on color frequency in the painting
const rileyColors: WeightedColor[] = [
  [25, 35, 75, 10],    // dark navy - dominant
  [65, 90, 165, 8],    // royal blue - very common
  [100, 140, 200, 7],  // medium blue - common
  [150, 170, 210, 5],  // periwinkle - moderate
  [180, 160, 200, 5],  // lavender - moderate
  [140, 100, 160, 3],  // purple - less common
  [200, 100, 140, 3],  // pink/magenta - sparse
  [230, 130, 100, 3],  // coral - sparse
  [240, 180, 80, 2],   // gold/yellow - accent
  [250, 220, 180, 2],  // cream white - thin stripes
  [80, 170, 140, 3],   // teal - sparse
  [50, 130, 100, 2],   // emerald green - accent
];

// Vivid primary/secondary colors for polyhedra face coloring
// Face types map to t values: tri=0.15, sq=0.40, pent=0.60, hex=0.75, oct=0.90
// Using direct lookup instead of interpolation for distinct colors
const primaryFaceColors: Record<number, [number, number, number]> = {
  3: [240, 50, 50],     // vivid red - triangles
  4: [255, 210, 0],     // vivid yellow - squares
  5: [30, 200, 80],     // vivid green - pentagons
  6: [50, 130, 255],    // vivid blue - hexagons
  8: [180, 50, 220],    // vivid purple - octagons
};

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h = h % 360;
  s = s / 100;
  l = l / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ];
}

function interpolatePalette(colors: WeightedColor[], t: number): [number, number, number] {
  if (t <= 0) return [colors[0][0], colors[0][1], colors[0][2]];
  if (t >= 1) {
    const last = colors[colors.length - 1];
    return [last[0], last[1], last[2]];
  }

  // Calculate cumulative weights to determine color positions
  const weights = colors.map(c => c[3] ?? 1);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  // Build cumulative positions based on weights
  const positions: number[] = [0];
  let cumulative = 0;
  for (let i = 0; i < weights.length - 1; i++) {
    cumulative += weights[i];
    positions.push(cumulative / totalWeight);
  }
  positions.push(1);

  // Find which segment t falls into
  let i = 0;
  for (let j = 0; j < positions.length - 1; j++) {
    if (t >= positions[j] && t <= positions[j + 1]) {
      i = j;
      break;
    }
  }

  // Interpolate within the segment
  const segmentStart = positions[i];
  const segmentEnd = positions[i + 1];
  const f = segmentEnd > segmentStart ? (t - segmentStart) / (segmentEnd - segmentStart) : 0;

  const c1 = colors[i];
  const c2 = colors[Math.min(i + 1, colors.length - 1)];

  return [
    Math.round(c1[0] + (c2[0] - c1[0]) * f),
    Math.round(c1[1] + (c2[1] - c1[1]) * f),
    Math.round(c1[2] + (c2[2] - c1[2]) * f)
  ];
}

export const colorPalettes: Record<string, ColorFunction> = {
  primary: (t) => {
    // Direct lookup for polyhedra face types (no interpolation)
    // tri=0.15, sq=0.40, pent=0.60, hex=0.75, oct=0.90
    if (t <= 0.05) return [40, 40, 40];
    if (t < 0.30) return primaryFaceColors[3];  // triangles
    if (t < 0.50) return primaryFaceColors[4];  // squares
    if (t < 0.68) return primaryFaceColors[5];  // pentagons
    if (t < 0.82) return primaryFaceColors[6];  // hexagons
    return primaryFaceColors[8];                 // octagons
  },

  midcentury: (t) => {
    if (t === 0) return [35, 31, 32];
    return interpolatePalette(midcenturyColors, t);
  },

  riley: (t) => {
    if (t === 0) return [25, 35, 75];
    return interpolatePalette(rileyColors, t);
  },

  penrose: (t) => {
    // Classic Penrose tiling colors - blue thick, green thin
    if (t < 0.5) return [106, 168, 79];   // green (thin rhombi, darts)
    return [111, 168, 220];               // light blue (thick rhombi, kites)
  },

  blueprint: (t) => {
    // Blue tones on grey - darker thick, lighter thin
    if (t < 0.5) return [91, 141, 190];   // lighter blue (thin)
    return [74, 111, 165];                // darker blue (thick)
  },

  vintage: (t) => {
    // Red on cream - classic vintage look
    if (t < 0.5) return [205, 92, 92];    // indian red (thin)
    return [245, 235, 220];               // cream (thick)
  }
};

/** List of available colour palette names */
export const colourPaletteOptions = Object.keys(colorPalettes);

/** Get a color function by name, with fallback to midcentury */
export function getColorPalette(name: string): ColorFunction {
  return colorPalettes[name] || colorPalettes.midcentury;
}
